import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { RepoMap } from '../types/environment';
import { normalizePrStatus, type NormalizedPrStatus } from '../utils/taskStatus';

interface TaskDetails {
    status?: string;
    prLinks: Record<string, string>;
    prStatuses: Record<string, NormalizedPrStatus>;
    turnID?: string;
}

const TaskContext = createContext<TaskDetails>({ prLinks: {}, prStatuses: {} });

function extractPRLinks(repoInfo?: RepoMap): Record<string, string> {
    if (!repoInfo) {
        return {};
    }
    const links: Record<string, string> = {};
    Object.entries(repoInfo).forEach(([repo, info]) => {
        if (info?.PRLink) {
            links[repo] = info.PRLink;
        }
    });
    return links;
}

function extractPRStatuses(repoInfo?: RepoMap): Record<string, NormalizedPrStatus> {
    if (!repoInfo) {
        return {};
    }
    const statuses: Record<string, NormalizedPrStatus> = {};
    Object.entries(repoInfo).forEach(([repo, info]) => {
        const normalized = normalizePrStatus(info?.PRStatus);
        if (normalized) {
            statuses[repo] = normalized;
        }
    });
    return statuses;
}

export function useTask() {
    return useContext(TaskContext);
}

export function TaskProvider({ children }: { children: React.ReactNode }) {
    const location = useLocation();
    const [status, setStatus] = useState<string | undefined>();
    const [prLinks, setPRLinks] = useState<Record<string, string>>({});
    const [prStatuses, setPRStatuses] = useState<Record<string, NormalizedPrStatus>>({});
    const [turnID, setTurnID] = useState<string | undefined>();
    const streamRef = useRef<EventSource | null>(null);
    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Match /tasks/:taskID or /workstreams/tasks/:workstreamID/:taskID
        const directMatch = location.pathname.match(/^\/tasks\/([^/]+)/);
        const workstreamMatch = location.pathname.match(/^\/workstreams\/tasks\/([^/]+)\/([^/]+)/);
        const taskID = workstreamMatch ? workstreamMatch[2] : directMatch ? directMatch[1] : undefined;
        const workstreamID = workstreamMatch ? workstreamMatch[1] : undefined;
        const wsQuery = workstreamID ? `?workstreamID=${encodeURIComponent(workstreamID)}` : '';
        const isCreateTaskRoute = taskID === 'new';

        // Close any existing stream when task changes or is cleared
        if (streamRef.current) {
            streamRef.current.close();
            streamRef.current = null;
        }

        if (pollTimeoutRef.current !== null) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }

        if (!taskID || isCreateTaskRoute) {
            setStatus(undefined);
            setPRLinks({});
            setPRStatuses({});
            setTurnID(undefined);
            return;
        }

        const abort = new AbortController();
        let eventSource: EventSource | null = null;
        let currentTurnID: string | undefined = undefined;
        const terminalStatus = new Set(['Done', 'Failed']);

        const closeStream = () => {
            if (eventSource) {
                const current = eventSource;
                current.close();
                if (streamRef.current === current) {
                    streamRef.current = null;
                }
                eventSource = null;
            }
        };

        const openStream = (turn: string) => {
            if (abort.signal.aborted) {
                return;
            }
            if (eventSource && currentTurnID === turn) {
                return;
            }
            closeStream();
            const newEventSource = new EventSource(`/api/tasks/${taskID}/turns/${turn}/logs${wsQuery}`);
            eventSource = newEventSource;
            streamRef.current = newEventSource;
            let handledClose = false;
            newEventSource.onmessage = () => {};
            newEventSource.onerror = () => {
                if (abort.signal.aborted || handledClose) return;
                handledClose = true;
                const current = newEventSource;
                current?.close();
                if (streamRef.current === current) {
                    streamRef.current = null;
                }
                if (eventSource === current) {
                    eventSource = null;
                }
                scheduleStatusRefresh();
            };
        };

        const scheduleStatusRefresh = () => {
            if (abort.signal.aborted) {
                return;
            }
            if (pollTimeoutRef.current !== null) {
                clearTimeout(pollTimeoutRef.current);
            }
            pollTimeoutRef.current = setTimeout(() => {
                pollTimeoutRef.current = null;
                if (abort.signal.aborted) {
                    return;
                }
                fetchStatus().then((_data) => {
                    if (abort.signal.aborted) {
                        return;
                    }
                    scheduleStatusRefresh();
                });
            }, 5000);
        };

        const handleStatusUpdate = (
            _data: { Status?: string } | undefined
        ) => {
            if (abort.signal.aborted) {
                return;
            }
            scheduleStatusRefresh();
        };

        async function fetchStatus() {
            try {
               const resp = await fetch(
                    `/api/tasks/${taskID}${wsQuery}`,
                    { signal: abort.signal }
                );
                if (!resp.ok) return undefined;
                const data: { Status?: string; RepoInfo?: RepoMap; TurnID?: string } = await resp.json();
                if (abort.signal.aborted) return undefined;
                setStatus(data.Status);
                setPRLinks(extractPRLinks(data.RepoInfo));
                setPRStatuses(extractPRStatuses(data.RepoInfo));
                setTurnID(data.TurnID);

                const turnChanged = data.TurnID !== currentTurnID;
                const isTerminal = terminalStatus.has(data.Status ?? '');

                if (turnChanged || isTerminal) {
                    closeStream();
                }

                if (data.TurnID && !isTerminal) {
                    openStream(data.TurnID);
                }

                currentTurnID = data.TurnID;
                return data;
            } catch {
                return undefined;
            }
        }

        fetchStatus().then((data) => {
            if (abort.signal.aborted) return;
            handleStatusUpdate(data);
        });

        return () => {
            abort.abort();
            closeStream();
            if (streamRef.current) {
                streamRef.current.close();
                streamRef.current = null;
            }
            if (pollTimeoutRef.current !== null) {
                clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
        };
    }, [location]);

    return (
        <TaskContext.Provider value={{ status, prLinks, prStatuses, turnID }}>
            {children}
        </TaskContext.Provider>
    );
}
